/**
 * Iter XX.200 — Meta App Events consent (frontend surface).
 *
 * Consent-gated Facebook Android SDK init (founder-approved reversal
 * of the XX.198 "no Facebook Android SDK" rule for App Promotion
 * attribution).
 *
 * Contract:
 *   · Web / iOS: this module is a no-op (the plugin is Android-only).
 *   · Android:   calls the `MetaConsent` native plugin registered in
 *                MainActivity (see runbook §4). Decision persists in
 *                localStorage AND — for server visibility into
 *                allow/decline rates — is mirrored to
 *                POST /api/web/meta-consent (fire-and-forget).
 *
 * Never throws. Every call resolves.
 */
import { Capacitor, registerPlugin } from "@capacitor/core";
import api from "./api";

const STORAGE_KEY = "gully_meta_consent_v1";  // "granted" | "denied"
const STORAGE_TS_KEY = "gully_meta_consent_v1_ts";

const MetaConsentPlugin = registerPlugin("MetaConsent", {
  // Web fallback — the plugin is Android-only. Every method resolves
  // to a no-op so callers don't have to branch.
  web: {
    async grant() { return { ok: true, platform: "web" }; },
    async revoke() { return { ok: true, platform: "web" }; },
    async isInitialized() { return { initialized: false }; },
  },
});

/** True iff we're running on the Capacitor Android shell. */
export function isAndroidNative() {
  try {
    return Capacitor.getPlatform() === "android" && Capacitor.isNativePlatform();
  } catch { return false; }
}

/** Iter XX.210 — True iff we're running on the Capacitor iOS shell.
 *  The MetaConsent plugin is now registered on iOS too (native Swift
 *  bridge mirroring the Android Java plugin). Consent-gated init
 *  contract identical to Android: SDK dormant until grant(). */
export function isIOSNative() {
  try {
    return Capacitor.getPlatform() === "ios" && Capacitor.isNativePlatform();
  } catch { return false; }
}

/** True iff we're on ANY native shell where MetaConsent is registered. */
export function isMetaConsentNative() {
  return isAndroidNative() || isIOSNative();
}

/** Read the persisted decision (never blocks, never throws). */
export function readMetaConsent() {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === "granted" || v === "denied") return v;
  } catch { /* localStorage unavailable */ }
  return null;
}

/**
 * True if we should surface the consent bar right now.
 *
 * Iter XX.210 — Bar shows on BOTH Android and iOS. One-time per install
 * (device localStorage decides — wiped on uninstall on both platforms;
 * iOS Keychain-persisted identity is reconciled at boot by
 * `nativeTokenStorage._ensureFreshInstallSentinel` so this stays a true
 * per-install one-time surface). Caller must ensure TOS is already
 * accepted before calling (Chat.jsx renders the ConsentBar first; the
 * MetaConsentBar sits BELOW it in the DOM so TOS is always tapped
 * first on a fresh install).
 *
 * Iter XX.212 — SYNCHRONOUS. If the Capacitor bridge isn't ready yet
 * on iOS at the moment this runs (Chat.jsx mounts its useEffect before
 * WKWebView finishes injecting the `Capacitor` global on very cold
 * launches), this returns `false`. Callers that need bridge-aware
 * behaviour (like the Chat.jsx mount effect) MUST use
 * `evaluateMetaConsentBarEligibility()` below, which awaits the bridge
 * with the same bounded-wait discipline as `replayMetaConsentOnBoot`.
 * The sync gate remains exported for tests and the Dev Reset panel
 * (post-hydration synchronous re-check).
 */
export function shouldShowMetaConsentBar({ tosAccepted } = {}) {
  if (!isMetaConsentNative()) return false;
  if (!tosAccepted) return false;
  return readMetaConsent() === null;
}

async function _writeAudit(decision, extra = {}) {
  try {
    await api.post("/web/meta-consent", {
      decision,
      platform: Capacitor.getPlatform(),
      ...extra,
    });
  } catch { /* audit is fire-and-forget */ }
}

/**
 * Allow Meta measurement. Persists locally, initialises the SDK
 * natively, and mirrors the decision to the server.
 */
export async function grantMetaConsent(extra = {}) {
  try { localStorage.setItem(STORAGE_KEY, "granted"); } catch { /* noop */ }
  try { localStorage.setItem(STORAGE_TS_KEY, String(Date.now())); } catch { /* noop */ }
  let native = { ok: false, platform: "web" };
  if (isMetaConsentNative()) {
    try {
      native = await MetaConsentPlugin.grant();
    } catch (e) {
      native = { ok: false, error: String(e && e.message ? e.message : e) };
    }
  }
  _writeAudit("granted", { native_ok: !!native.ok, ...extra });
  return { ok: true, native };
}

/**
 * Decline Meta measurement. Persists locally, revokes/keeps the SDK
 * uninitialised, and mirrors the decision to the server.
 */
export async function denyMetaConsent(extra = {}) {
  try { localStorage.setItem(STORAGE_KEY, "denied"); } catch { /* noop */ }
  try { localStorage.setItem(STORAGE_TS_KEY, String(Date.now())); } catch { /* noop */ }
  let native = { ok: false, platform: "web" };
  if (isMetaConsentNative()) {
    try {
      native = await MetaConsentPlugin.revoke();
    } catch (e) {
      native = { ok: false, error: String(e && e.message ? e.message : e) };
    }
  }
  _writeAudit("denied", { native_ok: !!native.ok, ...extra });
  return { ok: true, native };
}

/**
 * Iter XX.200-b — Boot-time consent replay (Android only).
 *
 * Founder contract (hardened):
 *   · consent absent  → TERMINAL, no native grant, flag set
 *   · consent denied  → TERMINAL, no native grant, flag set
 *   · consent granted → bounded bridge wait (up to 10s), then
 *                       exactly one native grant call
 *   · native grant OK → TERMINAL, no further calls this process
 *   · native grant failure (throw / ok=false) → TERMINAL diagnostic,
 *                       no infinite loop
 *   · bridge_not_ready after bounded wait → NOT terminal — the
 *                       app-resume safety net will re-attempt on
 *                       next foreground; consent gating preserved
 *                       (grant only fires under decision === "granted")
 *
 * `_REPLAY_ATTEMPTED` becomes true ONLY on terminal outcomes:
 *   1. not_android
 *   2. consent_absent
 *   3. consent_denied
 *   4. native grant returned ok=true       ("granted")
 *   5. native grant returned ok=false      ("plugin_reported_failure")
 *   6. native grant threw                  ("plugin_threw")
 *
 * It stays false on `bridge_not_ready` so an unusually cold device
 * can still recover on foreground.
 *
 * Every terminal state posts to /api/web/meta-consent with
 * decision="replay" and a `reason` diagnostic so the founder can
 * audit the fleet without device-side logcat.
 */
let _REPLAY_ATTEMPTED = false;
let _RESUME_LISTENER_REGISTERED = false;

async function _waitForNativeBridge(maxWaitMs = 10000, pollMs = 100) {
  const start = Date.now();
  while (Date.now() - start < maxWaitMs) {
    try {
      if (Capacitor && typeof Capacitor.isNativePlatform === "function"
          && Capacitor.isNativePlatform()) {
        return true;
      }
    } catch { /* Capacitor still loading */ }
    await new Promise((r) => setTimeout(r, pollMs));
  }
  return false;
}

async function _registerResumeSafetyNet() {
  // Belt-and-braces for `bridge_not_ready`: if the bounded wait
  // times out on an unusually cold launch, re-attempt on the next
  // Capacitor App resume. `_REPLAY_ATTEMPTED` still gates the
  // ultimate outcome, so this can never double-fire a grant.
  if (_RESUME_LISTENER_REGISTERED) return;
  _RESUME_LISTENER_REGISTERED = true;
  try {
    const mod = await import("@capacitor/app");
    const App = mod && (mod.App || (mod.default && mod.default.App));
    if (App && typeof App.addListener === "function") {
      await App.addListener("appStateChange", (state) => {
        if (state && state.isActive && !_REPLAY_ATTEMPTED) {
          replayMetaConsentOnBoot().catch(() => { /* fire-and-forget */ });
        }
      });
    }
  } catch { /* @capacitor/app missing — safety net simply absent */ }
}

/**
 * Iter XX.212 — bridge-aware Meta consent bar eligibility.
 *
 * Root cause it addresses: on physical iOS devices, WKWebView starts
 * running our JS bundle a few dozen milliseconds BEFORE the Capacitor
 * bridge injects the `Capacitor` global. Chat.jsx's post-mount effect
 * ran once, synchronously called `shouldShowMetaConsentBar()`, which
 * saw `Capacitor.isNativePlatform() === false` and locked the bar OFF
 * for the whole session. Users saw TOS → tap accept → composer, with
 * no Meta bar in between.
 *
 * Contract:
 *   · Waits up to `maxWaitMs` for the native bridge (same bounded-poll
 *     discipline as `replayMetaConsentOnBoot`).
 *   · Once the bridge reports native, re-evaluates the SAME gate
 *     `shouldShowMetaConsentBar({ tosAccepted })` uses. Zero divergence
 *     between the sync and async gates — this method just delays the
 *     read past the bridge race.
 *   · Web: bridge never reports native → returns false after the wait.
 *     Falls back to the sync gate result so pure-web browsers keep
 *     paying zero cost (they don't wait at all: the sync path returns
 *     false immediately when `isMetaConsentNative()` is false and the
 *     helper below short-circuits on non-native platforms first).
 *   · Never throws. Callers can `await` this and safely `setState`.
 *
 * Return shape:
 *   { visible: boolean, reason:
 *     "shown" | "bridge_not_ready" | "already_decided" |
 *     "not_native" | "tos_not_accepted" }
 */
export async function evaluateMetaConsentBarEligibility(
  { tosAccepted } = {},
  { maxWaitMs = 10000, pollMs = 100 } = {},
) {
  // Cheap paths first — no bridge wait needed.
  if (!tosAccepted) return { visible: false, reason: "tos_not_accepted" };
  if (readMetaConsent() !== null) {
    return { visible: false, reason: "already_decided" };
  }

  // On a plain browser this exits immediately (the platform check
  // inside isMetaConsentNative() sees "web" from the very first tick,
  // no bridge involved). On iOS/Android native we may need to wait
  // for the Capacitor global to attach.
  if (isMetaConsentNative()) {
    // Already native from tick 0. Nothing to wait for.
    return { visible: true, reason: "shown" };
  }

  // We might be on a physical iOS device where the bridge hasn't
  // attached yet. Poll with the same bounded discipline the boot-
  // replay path uses. Web callers exit this loop by timing out — a
  // ~10s worst case is acceptable because Chat.jsx renders the
  // consent bar asynchronously and the bar simply appears late (or
  // never, on web) rather than blocking anything on screen.
  const ready = await _waitForNativeBridge(maxWaitMs, pollMs);
  if (!ready) return { visible: false, reason: "bridge_not_ready" };

  // Bridge became ready — re-run the sync gate now that
  // `isMetaConsentNative()` will report accurately.
  if (!isMetaConsentNative()) {
    return { visible: false, reason: "not_native" };
  }
  if (readMetaConsent() !== null) {
    return { visible: false, reason: "already_decided" };
  }
  return { visible: true, reason: "shown" };
}

export async function replayMetaConsentOnBoot() {
  // Fast rejects that do not need the flag or an audit row.
  const platform = (() => {
    try { return Capacitor.getPlatform(); } catch { return "web"; }
  })();
  if (platform !== "android" && platform !== "ios") {
    _REPLAY_ATTEMPTED = true;   // terminal — no retry ever on web
    return { ok: true, replayed: false, reason: "not_native" };
  }
  if (_REPLAY_ATTEMPTED) {
    return { ok: true, replayed: false, reason: "already_attempted_this_process" };
  }

  const decision = readMetaConsent();
  // Iter XX.269 — Xcode-visible breadcrumb at the earliest gate.
  try {
    // eslint-disable-next-line no-console
    console.info("[XX.269-meta] replay_start", {
      platform, decision_raw: decision,
    });
  } catch { /* noop */ }

  // TERMINAL: consent absent — MUST NOT invoke native grant.
  if (decision === null) {
    _REPLAY_ATTEMPTED = true;
    _writeAudit("replay", { native_ok: false, reason: "consent_absent" });
    try {
      // eslint-disable-next-line no-console
      console.info("[XX.269-meta] replay_terminal consent_absent");
    } catch { /* noop */ }
    return { ok: true, replayed: false, reason: "consent_absent" };
  }
  // TERMINAL: consent denied — MUST NOT invoke native grant.
  if (decision !== "granted") {
    _REPLAY_ATTEMPTED = true;
    _writeAudit("replay", { native_ok: false, reason: "consent_denied" });
    try {
      // eslint-disable-next-line no-console
      console.info("[XX.269-meta] replay_terminal consent_denied");
    } catch { /* noop */ }
    return { ok: true, replayed: false, reason: "consent_denied" };
  }

  // consent === "granted" from here on. Register the resume safety
  // net BEFORE the wait so a bridge_not_ready outcome has a retry
  // path on foreground.
  await _registerResumeSafetyNet();

  const bridgeReady = await _waitForNativeBridge();
  if (!bridgeReady) {
    // NON-terminal — do NOT set _REPLAY_ATTEMPTED. Log the
    // diagnostic so the fleet-level rate is observable.
    _writeAudit("replay", { native_ok: false, reason: "bridge_not_ready" });
    return { ok: false, replayed: false, reason: "bridge_not_ready" };
  }

  try {
    const result = await MetaConsentPlugin.grant();
    const nativeOk = !!(result && result.ok);
    _REPLAY_ATTEMPTED = true;   // terminal either way
    _writeAudit("replay", {
      native_ok: nativeOk,
      reason: nativeOk ? "granted" : "plugin_reported_failure",
    });
    try {
      console.info("[metaConsent] boot replay",
        { decision: "granted", native_ok: nativeOk, result });
      // Iter XX.269 — Xcode-visible mirror. `[XX.269-meta]` prefix
      // is the canonical grep target the founder uses in Xcode
      // device log to trace the full boot lifecycle without Safari
      // Web Inspector.
      // eslint-disable-next-line no-console
      console.info("[XX.269-meta] boot_replay_result",
        { decision: "granted", native_ok: nativeOk, plugin_reason: result?.reason || null });
    } catch { /* noop */ }
    return {
      ok:       nativeOk,
      replayed: nativeOk,
      reason:   nativeOk ? "granted" : "plugin_reported_failure",
      native:   result,
    };
  } catch (e) {
    _REPLAY_ATTEMPTED = true;   // terminal — prevent retry-loop
    const msg = String(e && e.message ? e.message : e);
    _writeAudit("replay", { native_ok: false, reason: "plugin_threw" });
    try { console.warn("[metaConsent] boot replay threw", msg); }
    catch { /* noop */ }
    return { ok: false, replayed: false, reason: "plugin_threw", error: msg };
  }
}

/** Testing hook — reset the per-process guards. Not exported by default. */
export function __resetReplayGuardForTests() {
  _REPLAY_ATTEMPTED = false;
  _RESUME_LISTENER_REGISTERED = false;
}
